import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { getCurrentCoords } from "../lib/geo";
import { useAuth } from "../auth/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  LogOut,
  CalendarDays,
  MapPin,
  AlertTriangle,
  UserCircle2,
  Settings,
  Target,
  BadgeCheck,
  Play,
  Navigation,
  CheckCircle2,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { fmtTime, to12h, ROLE_LABEL, formatUSPhone } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { ReleaseNotesAnnouncer } from "../components/ReleaseNotesAnnouncer";
import { LocationPermissionPrimer } from "../components/LocationPermissionPrimer";
import { JobPriorityPill, derivedUrgency } from "../components/JobPriorityPill";
import { SwipeableRow, clientQuickActions } from "../components/SwipeableRow";
import { LeaderMaterials } from "../components/LeaderMaterials";

function startOfWeek(d = new Date()) {
  const x = new Date(d);
  const day = x.getDay() || 7; // Mon..Sun → 1..7
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - (day - 1));
  return x;
}

function endOfWeek(d = new Date()) {
  const s = startOfWeek(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
}

function iso(d) { return d.toISOString(); }

export default function MobileHome() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [weekJobs, setWeekJobs] = useState([]);
  const [clients, setClients] = useState({});
  const [teams, setTeams] = useState({});
  const [users, setUsers] = useState({});
  const [materialSummary, setMaterialSummary] = useState({ isLeader: false, openCount: 0 });
  const [release, setRelease] = useState({ release_time: null, locked: false, is_admin: false });
  const [promoting, setPromoting] = useState(false);
  const [completingDay, setCompletingDay] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await load();
      toast.success(t("Agenda atualizada"));
    } catch (e) {
      toast.error(t("Não foi possível atualizar. Tente novamente."));
    } finally {
      setRefreshing(false);
    }
  }

  async function load() {
    const ws = startOfWeek();
    // Carregamos a semana atual + um horizonte de 60 dias à frente, para que,
    // quando a semana atual já não tiver casas pela frente, possamos mostrar
    // as PRÓXIMAS casas agendadas (mesmo que sejam de semanas futuras).
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 60);
    horizon.setHours(23, 59, 59, 999);
    const [j, c, rel, t, u] = await Promise.all([
      api.get(`/jobs?from=${iso(ws)}&to=${iso(horizon)}`),
      api.get("/clients"),
      api.get("/schedule/release-config").catch(() => ({ data: { locked: false } })),
      api.get("/teams"),
      api.get("/collaborators?include_admin=true").catch(() => ({ data: [] })),
    ]);
    setWeekJobs(j.data);
    setClients(Object.fromEntries(c.data.map((x) => [x.id, x])));
    setRelease(rel.data || { locked: false });
    setTeams(Object.fromEntries((t.data || []).map((x) => [x.id, x])));
    setUsers(Object.fromEntries((u.data || []).map((x) => [x.id, x])));
  }
  useEffect(() => {
    load();
    // Poll the release config every minute so the agenda auto-unlocks as
    // soon as the cut-off time hits (no need for the operator to refresh).
    const t = setInterval(() => {
      api
        .get("/schedule/release-config")
        .then((r) => setRelease(r.data || { locked: false }))
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(t);
  }, []);

  const todayKey = new Date().toDateString();
  const todayJobs = useMemo(() => {
    return weekJobs
      .filter((j) => new Date(j.scheduled_at).toDateString() === todayKey)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  }, [weekJobs, todayKey]);

  // Colaborador NÃO deve ver casas de dias que já passaram (elas seguem
  // visíveis para o admin na Agenda). Mostramos apenas hoje + dias futuros
  // da semana. Admin/supervisor que abrirem a home mobile veem tudo.
  const isCollaborator = user?.role === "collaborator";
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const weekEnd = useMemo(() => endOfWeek(), []);

  // Casas da SEMANA ATUAL (Seg–Dom) que têm cliente válido.
  const weekScoped = useMemo(() => {
    const ws = startOfWeek();
    return [...weekJobs]
      .filter((j) => clients[j.client_id])
      .filter((j) => {
        const d = new Date(j.scheduled_at);
        return d >= ws && d <= weekEnd;
      })
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  }, [weekJobs, clients, weekEnd]);

  // Colaborador NÃO deve ver casas de dias que já passaram nesta semana.
  // Admin/supervisor que abrirem a home mobile veem a semana inteira.
  const visibleWeekJobs = useMemo(() => {
    return weekScoped.filter(
      (j) => !isCollaborator || new Date(j.scheduled_at) >= todayStart,
    );
  }, [weekScoped, isCollaborator, todayStart]);
  const visibleFinished = visibleWeekJobs.filter((j) => j.status === "finalizada").length;

  // Opção C: quando a semana atual já não tem casas pela frente, mostramos as
  // PRÓXIMAS casas agendadas (de semanas futuras) que vieram no horizonte.
  const upcomingLater = useMemo(() => {
    if (visibleWeekJobs.length > 0) return [];
    return [...weekJobs]
      .filter((j) => clients[j.client_id])
      .filter((j) => new Date(j.scheduled_at) > weekEnd)
      .filter((j) => j.status !== "cancelada")
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
      .slice(0, 10);
  }, [visibleWeekJobs, weekJobs, clients, weekEnd]);

  // "Marcar dia como concluído" — fecha as casas de hoje que ficaram pendentes.
  const todayDateStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const todayPending = useMemo(
    () => todayJobs.filter((j) => j.status !== "finalizada" && j.status !== "cancelada"),
    [todayJobs],
  );
  async function completeDay() {
    setCompletingDay(true);
    try {
      const { data } = await api.post("/jobs/complete-day", {
        date: todayDateStr,
        job_ids: todayPending.map((j) => j.id),
      });
      toast.success(t("Dia concluído — {n} casa(s) finalizadas hoje", { n: data.completed }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Falha ao concluir o dia"));
    } finally {
      setCompletingDay(false);
    }
  }

  // The "current focus" is:
  //   1) any job in `andamento` (top priority)
  //   2) else any job in `deslocamento`
  //   3) else the next `agendada` of today
  const currentJob = useMemo(() => {
    const live = todayJobs.find((j) => j.status === "andamento");
    if (live) return live;
    const moving = todayJobs.find((j) => j.status === "deslocamento");
    if (moving) return moving;
    return todayJobs.find((j) => j.status === "agendada");
  }, [todayJobs]);

  // Auto-promote the next 'agendada' to 'deslocamento' once the previous one
  // is finalized. Runs once per load(). Only PATCHes if there is NO other job
  // already in `deslocamento` or `andamento` (avoids double-promotion).
  useEffect(() => {
    if (promoting || todayJobs.length === 0) return;
    const hasActive = todayJobs.some((j) => j.status === "andamento" || j.status === "deslocamento");
    if (hasActive) return;
    // walk in order; find first agendada that comes after a finalizada
    let prevWasFinalized = false;
    let target = null;
    for (const j of todayJobs) {
      if (j.status === "finalizada") prevWasFinalized = true;
      else if (j.status === "agendada" && prevWasFinalized) {
        target = j;
        break;
      }
    }
    if (!target) return;
    setPromoting(true);
    api
      .post(`/jobs/${target.id}/depart`)
      .then(() => load())
      .catch(() => {})
      .finally(() => setPromoting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayJobs]);

  return (
    <div
      className="min-h-screen bg-[var(--cm-bg)] pb-24"
      data-testid="mobile-home"
    >
      <ReleaseNotesAnnouncer />
      <header className="bg-[var(--cm-ink)] text-white px-5 pt-6 pb-8 rounded-b-3xl relative overflow-hidden">
        <div className="absolute inset-0 cm-grid-bg opacity-10" />
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <div>
              <div className="font-bold">Clean Manager USA</div>
              <div className="text-[11px] uppercase tracking-widest text-blue-200">
                {ROLE_LABEL[user?.role]}
              </div>
            </div>
          </div>
          <button
            data-testid="mobile-logout"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className="p-2"
          >
            <LogOut size={18} />
          </button>
        </div>
        <div className="relative z-10 mt-5 flex items-center gap-3">
          <Link
            to="/perfil"
            data-testid="mobile-profile-link"
            className="w-14 h-14 rounded-2xl bg-blue-600/30 border border-blue-500/40 overflow-hidden flex items-center justify-center text-blue-100 hover:bg-blue-600/50"
          >
            {user?.photo_path ? (
              <img
                src={fileUrl(user.photo_path)}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <UserCircle2 size={28} />
            )}
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-blue-200">
              {t("Olá,")}
            </div>
            <div className="text-2xl font-bold truncate">{user?.name}</div>
            {user?.position && (
              <div className="mt-1">
                <span
                  data-testid="mobile-user-cargo"
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-white/15 text-white border border-white/20"
                >
                  <BadgeCheck size={13} className="text-blue-200" />
                  {user.position}
                </span>
              </div>
            )}
            <div className="text-sm text-slate-300 mt-1.5 flex items-center gap-2">
              <CalendarDays size={14} />
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                day: "2-digit",
                month: "long",
              })}
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="mobile-refresh-btn"
            title={t("Atualizar agenda")}
            className="p-2 text-blue-200 hover:text-white disabled:opacity-60"
          >
            <RefreshCw
              size={20}
              className={refreshing ? "animate-spin" : ""}
            />
          </button>
          <Link
            to="/mobile/kiosk"
            data-testid="mobile-kiosk-link"
            title={t("Modo Foco — só o trabalho atual")}
            className="p-2 text-blue-200 hover:text-white"
          >
            <Target size={20} />
          </Link>
          <Link
            to="/perfil"
            data-testid="mobile-settings-link"
            className="p-2 text-blue-200 hover:text-white"
          >
            <Settings size={20} />
          </Link>
        </div>
      </header>
      <LocationPermissionPrimer />

      <main className="p-4 -mt-4 relative z-10 space-y-4">
        {/* Calendário semanal estilo strip (Sun-Sat) */}
        <WeekStrip jobs={weekJobs} />

        {/* Agenda locked banner — collaborator only */}
        {release.locked && (
          <div
            data-testid="mobile-schedule-locked"
            className="cm-card p-5 text-center bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200"
          >
            <div className="w-12 h-12 mx-auto rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
              <CalendarDays size={22} />
            </div>
            <div className="font-semibold text-slate-900">
              {release.release_mode === "manual"
                ? t("Aguardando liberação da agenda de hoje")
                : t("Sua agenda será liberada às {time}", { time: to12h(release.release_time) })}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {release.release_mode === "manual"
                ? t("Assim que o administrador liberar, suas casas aparecem aqui automaticamente.")
                : t("Aguarde o horário de liberação. Quando chegar, suas casas aparecem aqui automaticamente.")}
            </div>
          </div>
        )}

        {/* Banner de alerta de materiais (líder) — bate o olho e avisa o admin */}
        {!release.locked && materialSummary.isLeader && materialSummary.openCount > 0 && (
          <button
            data-testid="material-alert-banner"
            onClick={() =>
              document
                .getElementById("mobile-leader-materials")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            className="w-full flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-left active:scale-[0.99] transition-transform"
          >
            <span className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={18} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold text-amber-800">
                {materialSummary.openCount}{" "}
                {materialSummary.openCount === 1
                  ? t("material precisa de reposição")
                  : t("materiais precisam de reposição")}
              </span>
              <span className="block text-xs text-amber-700/80">
                {t("Toque para ver e avisar o admin")}
              </span>
            </span>
            <span className="text-xs font-bold text-amber-700 flex-shrink-0">→</span>
          </button>
        )}

        {/* Current focus card — only when there's something to do AND not locked */}
        {!release.locked && currentJob && (
          <CurrentJobCard
            job={currentJob}
            client={clients[currentJob.client_id]}
            onChanged={load}
          />
        )}

        {/* Esta semana (lista estilo Bookings) */}
        {!release.locked && (
          <div data-testid="mobile-week-list">
            <div className="flex items-center justify-between mb-2 px-1">
              <h2 className="text-lg font-bold text-slate-900">{t("Esta semana")}</h2>
              <span className="text-xs text-slate-500">
                {visibleFinished}/{visibleWeekJobs.length} {t("feitas")}
              </span>
            </div>

            {visibleWeekJobs.length === 0 && upcomingLater.length === 0 && (
              <div className="cm-card p-8 text-center text-sm text-slate-500">
                {t("Você não tem limpezas esta semana.")}
              </div>
            )}

            <div className="space-y-2.5">
              {visibleWeekJobs
                .map((j) => (
                  <BookingRow
                    key={j.id}
                    job={j}
                    client={clients[j.client_id]}
                    team={teams[j.team_id]}
                    users={users}
                    isCurrent={currentJob?.id === j.id}
                    canSeeContact={user?.role === "admin" || user?.role === "supervisor"}
                  />
                ))}
            </div>

            {/* Próximas casas — quando a semana atual já não tem nada pela
                frente, mostramos as próximas casas agendadas (semanas futuras). */}
            {visibleWeekJobs.length === 0 && upcomingLater.length > 0 && (
              <div data-testid="mobile-upcoming-list" className="mt-1">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <CalendarDays size={16} className="text-blue-600" />
                  <h3 className="text-base font-bold text-slate-900">
                    {t("Próximas casas")}
                  </h3>
                </div>
                <div className="space-y-2.5">
                  {upcomingLater.map((j) => (
                    <BookingRow
                      key={j.id}
                      job={j}
                      client={clients[j.client_id]}
                      team={teams[j.team_id]}
                      users={users}
                      isCurrent={false}
                      canSeeContact={user?.role === "admin" || user?.role === "supervisor"}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Concluir o dia — só é permitido quando TODAS as casas de hoje
                estiverem finalizadas. Enquanto houver pendentes, fica bloqueado. */}
            {isCollaborator && todayJobs.length > 0 && (
              todayPending.length > 0 ? (
                <div
                  data-testid="mobile-complete-day-blocked"
                  className="mt-4 w-full rounded-xl bg-slate-100 border border-slate-200 px-4 py-3 text-center"
                >
                  <div className="flex items-center justify-center gap-2 text-slate-500 font-semibold">
                    <CheckCircle2 size={18} />
                    {t("Marcar dia como concluído")}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {t("Finalize todas as casas para concluir o dia")} —{" "}
                    {t("faltam")} {todayPending.length}
                  </div>
                </div>
              ) : (
                <button
                  data-testid="mobile-complete-day-btn"
                  onClick={completeDay}
                  disabled={completingDay}
                  className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3 transition-colors"
                >
                  {completingDay ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={18} />
                  )}
                  {t("Marcar dia como concluído")}
                </button>
              )
            )}
          </div>
        )}

        {/* Materiais da equipe (líder) — acesso rápido na tela inicial */}
        {!release.locked && (
          <div id="mobile-leader-materials" className="mt-2" data-testid="mobile-leader-materials">
            <LeaderMaterials onSummary={setMaterialSummary} />
          </div>
        )}
      </main>
    </div>
  );
}

// --- helpers -----------------------------------------------------------------

function WeekStrip({ jobs }) {
  const days = useMemo(() => {
    const ws = startOfWeek();
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(ws);
      d.setDate(d.getDate() + i);
      const isToday = d.toDateString() === new Date().toDateString();
      const dayJobs = jobs.filter(
        (j) => new Date(j.scheduled_at).toDateString() === d.toDateString(),
      );
      const finished = dayJobs.filter((j) => j.status === "finalizada").length;
      return {
        label: d.toLocaleDateString("en-US", { weekday: "short" }),
        date: d.getDate(),
        count: dayJobs.length,
        finished,
        isToday,
      };
    });
  }, [jobs]);

  const monthLabel = useMemo(() => {
    const now = new Date();
    return now.toLocaleDateString("en-US", { month: "long", year: "numeric" })
      .replace(/^./, (c) => c.toUpperCase());
  }, []);

  return (
    <div
      data-testid="mobile-week-strip"
      className="rounded-2xl overflow-hidden shadow-lg"
      style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)" }}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <span
          className="text-sm font-semibold tracking-wide"
          style={{ color: "#dbeafe" }}
        >
          {monthLabel}
        </span>
        <CalendarDays size={18} style={{ color: "#dbeafe" }} />
      </div>
      <div className="grid grid-cols-7 px-2 pb-3 gap-1">
        {days.map((d) => {
          const isPast = d.count > 0 && d.finished === d.count;
          return (
            <div
              key={`day-${d.date}-${d.label}`}
              className="flex flex-col items-center py-2"
            >
              <div
                className="text-[11px] uppercase font-bold tracking-wide"
                style={{ color: "#bfdbfe" }}
              >
                {d.label}
              </div>
              <div
                className={`mt-1 w-9 h-9 rounded-full flex items-center justify-center text-base font-extrabold relative ${
                  d.isToday ? "bg-white" : ""
                }`}
                style={{
                  color: d.isToday ? "#1d4ed8" : "#ffffff",
                }}
              >
                {d.date}
                {d.count > 0 && !d.isToday && (
                  <span
                    className="absolute -bottom-1 w-1.5 h-1.5 rounded-full"
                    style={{
                      background: isPast ? "#86efac" : "#fde68a",
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Linha de booking estilo print: status à direita, type label (1 Bedroom etc),
 * nome do cliente + endereço, e a equipe (verde) no rodapé.
 */
function BookingRow({ job, client, team, users, isCurrent, canSeeContact }) {
  const { t } = useI18n();
  const teamColor = team?.color || "#2563EB";
  const members = (team?.member_ids || []).map((id) => users[id]).filter(Boolean);
  const live = job.status === "andamento";
  const priority = job.priority || derivedUrgency(job.scheduled_at, job.status);
  const c = client;

  const fullAddress = useMemo(() => {
    if (!c) return "";
    return [c.address, c.city, c.state, c.zip_code].filter(Boolean).join(", ");
  }, [c]);

  const dateStr = useMemo(() => {
    try {
      const d = new Date(job.scheduled_at);
      return d.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }) + ", " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    } catch {
      return job.scheduled_at;
    }
  }, [job.scheduled_at]);

  function openMaps() {
    if (!fullAddress) return;
    window.open(
      `https://maps.google.com/?q=${encodeURIComponent(fullAddress)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  return (
    <SwipeableRow
      onSwipeRight={fullAddress ? openMaps : undefined}
      quickActions={canSeeContact ? clientQuickActions(c) : []}
      testId={`mobile-job-${job.id}`}
    >
      <Link
        to={`/mobile/job/${job.id}`}
        data-testid={`mobile-job-${job.id}-link`}
        style={{ borderLeftColor: teamColor, borderLeftWidth: 5 }}
        className={`block bg-white px-3.5 py-3 transition-all hover:shadow-sm ${
          isCurrent ? "ring-2 ring-blue-300" : ""
        } ${live ? "job-live" : ""}`}
      >
        {/* Cabeçalho: data + status */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-slate-600 font-mono">{dateStr}</div>
          <StatusBadge status={job.status} job={job} />
        </div>

        {/* Tipo / título (nome do cliente) */}
        <div className="flex items-start gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles size={14} className="text-amber-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 text-[15px] truncate leading-tight">
              {c?.name || t("Cliente")}
            </div>
            {c?.observations && (
              <div className="text-[11px] text-amber-700 flex items-center gap-1 mt-0.5">
                <AlertTriangle size={11} /> {t("Observações")}
              </div>
            )}
          </div>
          {priority && <JobPriorityPill value={priority} size="sm" />}
        </div>

        {/* Endereço */}
        <div className="flex items-start gap-2 mb-2 pl-9">
          <MapPin size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-slate-600 leading-snug">
            {c?.address}
            {c?.apt_number ? ` · Apt ${c.apt_number}` : ""}
            {c?.city ? `, ${c.city}` : ""}
            {c?.state ? `, ${c.state}` : ""}
            {c?.zip_code ? ` ${c.zip_code}` : ""}
          </div>
        </div>

        {/* Equipe (nome da equipe + membros, na cor da equipe) */}
        {team && (
          <div className="pl-9 flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center justify-center w-5 h-5 rounded text-white text-[11px] font-bold"
              style={{ backgroundColor: teamColor }}
              title={team.name}
            >
              i
            </span>
            <span
              className="text-[13px] font-semibold truncate"
              style={{ color: teamColor }}
              data-testid="booking-team-name"
            >
              {team.name}
              {members.length > 0 &&
                ` · ${members.map((m) => m.name?.split(" ")[0]).join(", ")}`}
            </span>
          </div>
        )}

        {/* Hint de gestos — só na primeira vez (não persiste) */}
        <div className="pl-9 mt-2 text-[10px] text-slate-400 select-none hidden sm:block">
          {canSeeContact
            ? t("← deslize para WhatsApp · deslize para mapa →")
            : t("deslize para mapa →")}
        </div>
      </Link>
    </SwipeableRow>
  );
}

function CurrentJobCard({ job, client, onChanged }) {
  const { t } = useI18n();
  const [starting, setStarting] = useState(false);
  const [finishing, setFinishing] = useState(false);

  if (!job || !client) return null;

  async function getCoords() {
    return getCurrentCoords();
  }

  async function startNow() {
    setStarting(true);
    try {
      const { lat, lng } = await getCoords();
      await api.post(`/jobs/${job.id}/start`, { lat, lng });
      toast.success(t("Trabalho iniciado"));
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || t("Falha ao iniciar");
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  }

  const map = {
    agendada: {
      tone: "from-amber-500 to-orange-600",
      label: t("Próximo trabalho"),
      icon: <Navigation size={18} />,
      sub: t("Quando chegar no local, toque em 'Cheguei na casa'"),
    },
    deslocamento: {
      tone: "from-orange-500 to-rose-600",
      label: t("Em deslocamento"),
      icon: <Navigation size={18} className="animate-pulse" />,
      sub: t("Toque em 'Cheguei na casa' assim que estacionar"),
    },
    andamento: {
      tone: "from-emerald-500 to-emerald-700",
      label: t("Limpeza em andamento"),
      icon: <Play size={18} />,
      sub: t("Em andamento desde {time}", {
        time: job.start_time
          ? new Date(job.start_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
          : "—",
      }),
    },
  };
  const cfg = map[job.status] || map.agendada;
  const live = job.status === "andamento";

  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(
    `${client.address || ""} ${client.city || ""} ${client.state || ""} ${client.zip_code || ""}`,
  )}`;

  return (
    <div
      data-testid="mobile-current-job"
      className={`cm-card p-0 overflow-hidden border-transparent`}
    >
      <div className={`bg-gradient-to-br ${cfg.tone} text-white px-4 py-3`}>
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest opacity-90 flex items-center gap-1.5">
            {cfg.icon} {cfg.label}
          </div>
          <div className="text-[11px] font-mono bg-white/20 rounded px-2 py-0.5">
            {fmtTime(job.scheduled_at)}
          </div>
        </div>
        <div className="mt-2 text-xl font-bold leading-tight">
          {client.name}
        </div>
        <div className="mt-1 text-sm flex items-start gap-1.5 opacity-95">
          <MapPin size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            {client.address}
            {client.apt_number ? ` · Apt ${client.apt_number}` : ""}
            {client.city ? `, ${client.city}` : ""}
            {client.state ? `, ${client.state}` : ""}
            {client.zip_code ? ` ${client.zip_code}` : ""}
          </span>
        </div>
      </div>

      <div className="p-3 space-y-2">
        {/* Primary CTA — changes based on status */}
        {!live ? (
          <button
            type="button"
            data-testid="mobile-current-arrived"
            onClick={startNow}
            disabled={starting}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-base font-bold flex items-center justify-center gap-2 py-3 rounded-xl shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98]"
          >
            {starting ? (
              <>
                <Loader2 size={18} className="animate-spin" /> {t("Validando GPS…")}
              </>
            ) : (
              <>
                <CheckCircle2 size={18} /> {t("Cheguei na casa")}
              </>
            )}
          </button>
        ) : (
          <Link
            to={`/mobile/job/${job.id}?finish=1`}
            data-testid="mobile-current-finish"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-base font-bold flex items-center justify-center gap-2 py-3 rounded-xl shadow-lg shadow-emerald-500/30 transition-all"
          >
            <CheckCircle2 size={18} /> {t("Concluí a limpeza")}
          </Link>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Link
            to={`/mobile/job/${job.id}`}
            data-testid="mobile-current-open"
            className="cm-btn bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold flex items-center justify-center gap-1.5 py-2 rounded-lg"
          >
            {t("Ver detalhes")}
          </Link>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-current-route"
            className="cm-btn bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold flex items-center justify-center gap-1.5 py-2 rounded-lg"
          >
            <Navigation size={14} /> {t("Traçar rota")}
          </a>
        </div>
      </div>

      <div className="px-4 pb-3 text-xs text-slate-500">{cfg.sub}</div>
    </div>
  );
}


function TeamBadge({ team, members, teamColor, testId }) {
  if (!team) return null;
  const initials = (name) =>
    (name || "?")
      .split(" ")
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  return (
    <div
      data-testid={testId}
      className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2 flex-wrap"
    >
      <span
        className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white shadow-sm"
        style={{ backgroundColor: teamColor }}
      >
        {team.name}
      </span>
      <div className="flex -space-x-2">
        {members.slice(0, 4).map((m) => (
          <div
            key={m.id}
            title={m.name}
            className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
            style={{ backgroundColor: teamColor }}
          >
            {m.photo_path ? (
              <img
                src={m.photo_path.startsWith("/") ? m.photo_path : `/uploads/${m.photo_path}`}
                alt=""
                className="w-full h-full rounded-full object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              initials(m.name)
            )}
          </div>
        ))}
        {members.length > 4 && (
          <div className="w-7 h-7 rounded-full border-2 border-white bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-bold shadow-sm">
            +{members.length - 4}
          </div>
        )}
      </div>
      <span className="text-[11px] text-slate-500">
        {members.length === 0
          ? "Sem membros"
          : members.length === 1
            ? members[0].name?.split(" ")[0]
            : `${members.length} pessoas`}
      </span>
    </div>
  );
}
